/**
 * AlertFilterToolbar (Story 27.2, extended) — search Input + ViewSwitcher +
 * "Create Alert" brand button, plus a Funnel filter popover with tabbed
 * multiselect (Status / Platform / Client) and a row of active filter chips
 * below. Chrome (radii, borders, shadows, motion) mirrors the tasks/clients
 * toolbars per the design-system directive.
 *
 * NO react-query — a pure leaf rendered bare in tests (no QueryClient).
 */

import { useState } from "react";
import { Input, Button, cn } from "@provenleap/ui";
import {
  MagnifyingGlass,
  X,
  Funnel,
  Check,
  CaretDown,
  BellRinging,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { ViewSwitcher, type AlertViewMode } from "./ViewSwitcher";
import { ALERT_HIDEABLE_COLUMNS } from "./alertColumns";
import { ColumnVisibilityMenu } from "../shared/columnVisibility";

type FilterTab = "status" | "platform" | "client";

/** The two alert statuses (the toggle space). */
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
];

interface AlertFilterToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  viewMode: AlertViewMode;
  onViewModeChange: (mode: AlertViewMode) => void;
  statusFilter: string[];
  onStatusFilterChange: (statuses: string[]) => void;
  platformFilter: string[];
  onPlatformFilterChange: (platforms: string[]) => void;
  clientFilter: string[];
  onClientFilterChange: (clients: string[]) => void;
  platformOptions: { value: string; label: string }[];
  clientOptions: { id: string; name: string }[];
  canManage?: boolean;
  onCreate?: () => void;
  /** Column-visibility (table view only). Hidden column ids + toggle handler. */
  hiddenColumns?: string[];
  onToggleColumn?: (id: string) => void;
  /** Show the "Columns" control — only relevant in the table view. */
  showColumnsControl?: boolean;
}

export function AlertFilterToolbar({
  search,
  onSearchChange,
  viewMode,
  onViewModeChange,
  statusFilter,
  onStatusFilterChange,
  platformFilter,
  onPlatformFilterChange,
  clientFilter,
  onClientFilterChange,
  platformOptions,
  clientOptions,
  canManage = false,
  onCreate,
  hiddenColumns = [],
  onToggleColumn,
  showColumnsControl = false,
}: AlertFilterToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("status");
  const [filterSearch, setFilterSearch] = useState("");

  const totalFilterCount =
    statusFilter.length + platformFilter.length + clientFilter.length;

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: "status", label: "Status", count: statusFilter.length },
    { key: "platform", label: "Platform", count: platformFilter.length },
    { key: "client", label: "Client", count: clientFilter.length },
  ];

  const toggle = <T,>(list: T[], value: T, onChange: (next: T[]) => void) => {
    onChange(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
    );
  };

  const clearFilters = () => {
    onStatusFilterChange([]);
    onPlatformFilterChange([]);
    onClientFilterChange([]);
  };

  const tabSearchNoun =
    activeTab === "status"
      ? "statuses"
      : activeTab === "platform"
        ? "platforms"
        : "clients";

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: Search + Filter + View + Create */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search input */}
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground"
          />
          <Input
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search alerts..."
            aria-label="Search alerts"
            className="pl-9 pr-9 rounded-2xl text-foreground placeholder:text-foreground shadow-xs text-[13.5px]"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Columns dropdown — table view only (show/hide table columns) */}
        {showColumnsControl && onToggleColumn && (
          <ColumnVisibilityMenu
            columns={ALERT_HIDEABLE_COLUMNS}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggleColumn}
          />
        )}

        {/* Filters dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => {
              setFilterOpen(!filterOpen);
              setFilterSearch("");
            }}
          >
            <Funnel size={16} className="mr-1" />
            Filters
            {totalFilterCount > 0 && (
              <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalFilterCount}
              </span>
            )}
            <CaretDown size={12} className="ml-1" />
          </Button>

          <AnimatePresence>
            {filterOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => {
                    setFilterOpen(false);
                    setFilterSearch("");
                  }}
                />

                {/* Dropdown panel */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute left-0 top-full mt-2.5 z-50 w-72 bg-popover rounded-2xl shadow-md p-2"
                  style={{ border: "0.5px solid var(--hairline)" }}
                >
                  {/* Arrow pointing up */}
                  <div
                    className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                    style={{
                      borderLeft: "0.5px solid var(--hairline)",
                      borderTop: "0.5px solid var(--hairline)",
                    }}
                  />
                  {/* Tab bar */}
                  <div className="flex mx-4 pb-0">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.key);
                          setFilterSearch("");
                        }}
                        className={cn(
                          "flex-1 px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer",
                          activeTab === tab.key
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Search */}
                  <div className="px-2 pt-2 pb-0.5">
                    <div className="relative">
                      <MagnifyingGlass
                        size={16}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                      />
                      <input
                        type="text"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        placeholder={`Search ${tabSearchNoun}...`}
                        className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                        style={{ outline: "none", boxShadow: "none" }}
                      />
                    </div>
                  </div>

                  {/* Tab content */}
                  <div className="max-h-[260px] overflow-y-auto pt-2 pb-1">
                    {/* Status tab */}
                    {activeTab === "status" && (
                      <>
                        {STATUS_OPTIONS.filter(
                          (o) =>
                            !filterSearch ||
                            o.label
                              .toLowerCase()
                              .includes(filterSearch.toLowerCase()),
                        ).map((option) => {
                          const isSelected = statusFilter.includes(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                toggle(
                                  statusFilter,
                                  option.value,
                                  onStatusFilterChange,
                                )
                              }
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={cn(
                                  "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                  isSelected &&
                                    "bg-primary border-primary text-white",
                                )}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              {option.label}
                            </button>
                          );
                        })}
                      </>
                    )}

                    {/* Platform tab */}
                    {activeTab === "platform" && (
                      <>
                        {platformOptions.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No platforms yet
                          </div>
                        ) : (
                          [...platformOptions]
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .filter(
                              (p) =>
                                !filterSearch ||
                                p.label
                                  .toLowerCase()
                                  .includes(filterSearch.toLowerCase()),
                            )
                            .map((platform) => {
                              const isSelected = platformFilter.includes(
                                platform.value,
                              );
                              return (
                                <button
                                  key={platform.value}
                                  type="button"
                                  onClick={() =>
                                    toggle(
                                      platformFilter,
                                      platform.value,
                                      onPlatformFilterChange,
                                    )
                                  }
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white",
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  {platform.label}
                                </button>
                              );
                            })
                        )}
                      </>
                    )}

                    {/* Client tab */}
                    {activeTab === "client" && (
                      <>
                        {clientOptions.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No clients yet
                          </div>
                        ) : (
                          [...clientOptions]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter(
                              (c) =>
                                !filterSearch ||
                                c.name
                                  .toLowerCase()
                                  .includes(filterSearch.toLowerCase()),
                            )
                            .map((client) => {
                              const isSelected = clientFilter.includes(
                                client.id,
                              );
                              return (
                                <button
                                  key={client.id}
                                  type="button"
                                  onClick={() =>
                                    toggle(
                                      clientFilter,
                                      client.id,
                                      onClientFilterChange,
                                    )
                                  }
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white",
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  {client.name}
                                </button>
                              );
                            })
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* View switcher + Create — pushed to the right */}
        <div className="flex gap-4 sm:ml-auto">
          <ViewSwitcher view={viewMode} onViewChange={onViewModeChange} />

          {canManage && onCreate && (
            <Button
              variant="brand"
              className="shrink-0 rounded-2xl text-[13px] cursor-pointer"
              onClick={onCreate}
            >
              <BellRinging size={16} className="mr-1" />
              Create Alert
            </Button>
          )}
        </div>
      </div>

      {/* Row 2: Active filter chips */}
      {totalFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {/* Status chips */}
          {statusFilter.map((status) => (
            <span
              key={`status-${status}`}
              className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
            >
              {STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status}
              <button
                type="button"
                onClick={() => toggle(statusFilter, status, onStatusFilterChange)}
                className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                style={{ minWidth: 0, minHeight: 0 }}
              >
                <X size={14} />
              </button>
            </span>
          ))}
          {/* Platform chips */}
          {platformFilter.map((value) => {
            const label =
              platformOptions.find((p) => p.value === value)?.label ?? value;
            return (
              <span
                key={`platform-${value}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                {label}
                <button
                  type="button"
                  onClick={() =>
                    toggle(platformFilter, value, onPlatformFilterChange)
                  }
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          {/* Client chips */}
          {clientFilter.map((id) => {
            const name = clientOptions.find((c) => c.id === id)?.name ?? id;
            return (
              <span
                key={`client-${id}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                {name}
                <button
                  type="button"
                  onClick={() => toggle(clientFilter, id, onClientFilterChange)}
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={clearFilters}
            className="text-[13px] hover:text-foreground min-h-[44px] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
            style={{ color: "#5A7396" }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
